'use server';

/**
 * Every server action in CPCel.
 *
 * Kept in one module so the full list of writes the application exposes can be
 * read in one place — which is exactly what you want when reviewing what is
 * reachable from a browser.
 */
import { redirect } from 'next/navigation';
import { z } from 'zod';
import { getDb } from '@/lib/db/client';
import { getActor } from '@/lib/auth/session';
import { formatDate, formatDateTime } from '@/lib/dates';
import { toAmountString } from '@/lib/money';
import { errors } from '@/lib/errors';
import { assertCan } from '@/lib/permissions';
import {
  agentTargetSchema,
  branchSchema,
  changePasswordSchema,
  closingCorrectionRequestSchema,
  closingCorrectionResubmitSchema,
  closingCorrectionReviewSchema,
  correctionRequestSchema,
  correctionReviewSchema,
  createUserSchema,
  updateUserSchema,
  customerPaymentSchema,
  customerSchema,
  employeeAllowanceSchema,
  employeeSchema,
  employeeStatusSchema,
  kpiRuleSchema,
  monthKeySchema,
  payslipCancelSchema,
  payslipDraftSchema,
  payslipFinalizeSchema,
  payslipPaySchema,
  payslipReopenSchema,
  agentClosingSchema,
  dailyClosingSchema,
  lateDailyClosingSchema,
  dailyOpeningSchema,
  expenseSchema,
  forgotPasswordSchema,
  gpCommissionSchema,
  inventoryAdjustmentSchema,
  stockCountAmendSchema,
  stockCountEntrySchema,
  stockCountFinalizeSchema,
  loginSchema,
  ownerWithdrawalSchema,
  productSchema,
  purchaseSchema,
  reopenDaySchema,
  resetPasswordSchema,
  resolveConflictSchema,
  saleReturnSchema,
  saleSchema,
  salaryPaymentSchema,
  accountAdjustmentSchema,
  accountTransferSchema,
  cancelImportSchema,
  commissionSettlementSchema,
  commitImportSchema,
  financialAccountSchema,
  floatMovementSchema,
  mapImportSchema,
  openingBalancesSchema,
  redeemStoreCreditSchema,
  startImportSchema,
  supplierReturnSchema,
  sendToHouseSchema,
  receiveReplacementSchema,
  cancelHouseReturnSchema,
  cancelSubscriptionSchema,
  dataExportSchema,
  grantSupportAccessSchema,
  reactivateSubscriptionSchema,
  reportSubscriptionPaymentSchema,
  revokeSupportAccessSchema,
  voidSupplierReturnSchema,
  voidTransferSchema,
  supplierPaymentSchema,
  supplierSchema,
  voidSaleSchema,
} from '@/lib/validation/schemas';
import { idSchema } from '@/lib/validation/common';
import { defineAction, defineQuery, FINANCIAL_PATHS, type ActionResult } from './helpers';

import * as authService from '@/server/services/auth';
import { createSale, voidSale } from '@/server/services/sales';
import { createPurchase, voidPurchase } from '@/server/services/purchases';
import { createSupplier, paySupplier, updateSupplier } from '@/server/services/suppliers';
import { createCustomer, receiveCustomerPayment, searchCustomers, updateCustomer } from '@/server/services/customers';
import { createEmployee, setEmployeeStatus, updateEmployee } from '@/server/services/employees';
import { saveKpiRule } from '@/server/services/kpi';
import {
  cancelPayslip,
  draftBranchPayroll,
  draftPayslip,
  finalizePayslip,
  payPayslip,
  removeEmployeeAllowance,
  reopenPayslip,
  saveEmployeeAllowance,
} from '@/server/services/payroll';
import { createExpense, paySalary, voidExpense } from '@/server/services/expenses';
import { recordCommission, recordWithdrawal } from '@/server/services/income';
import { createAdjustment, createReturn } from '@/server/services/returns';
import {
  amendStockCountLine,
  finalizeStockCount,
  recordStockCount,
} from '@/server/services/stock-count';
import { closeDay, closeDayLate, openDay, reopenDay } from '@/server/services/closing';
import { closeAgentDay } from '@/server/services/agent-closing';
import {
  adjustAccount,
  createAccount,
  createTransfer,
  updateAccount,
  voidTransfer,
} from '@/server/services/accounts';
import { consumeFloat, topUpFloat } from '@/server/services/float';
import { settleCommission, voidSettlement } from '@/server/services/commission';
import {
  createSupplierReturn,
  returnablePurchases,
  voidSupplierReturn,
} from '@/server/services/supplier-returns';
import {
  cancelHouseReturn,
  faultyAwaitingHouse,
  receiveReplacement,
  sendToHouse,
} from '@/server/services/house-returns';
import { recordOpeningBalances } from '@/server/services/onboarding';
import { redeemStoreCreditAction as redeemStoreCreditService } from '@/server/services/store-credit';
import { cancelImport, commitImport, mapAndValidate, startImport } from '@/server/services/imports';
import { reportPayment } from '@/server/services/billing';
import { grantSupportAccess, revokeSupportAccess } from '@/server/services/support-access';
import { requestExport } from '@/server/services/data-export';
import { cancelSubscription, reactivateSubscription } from '@/server/services/cancellation';
import { requestCorrection, reviewCorrection } from '@/server/services/corrections';
import { setAgentTarget } from '@/server/services/targets';
import {
  cancelClosingCorrection,
  requestClosingCorrection,
  resubmitClosingCorrection,
  reviewClosingCorrection,
} from '@/server/services/closing-corrections';
import { createProduct, searchSellableProducts, updateProduct, findHandsetByImei } from '@/server/services/catalog';
import {
  createBranch,
  createUser,
  setUserStatus,
  updateBranch,
  updateUser,
} from '@/server/services/organization';
import { updateSettings, type SettingsKey, SETTINGS_KEYS } from '@/server/services/settings';
import { resolveSyncConflict } from '@/server/services/sync';
import { markAllNotificationsRead, markNotificationRead } from '@/server/services/notifications';
import { transaction } from '@/lib/db/client';

// ---------------------------------------------------------------------------
// Authentication
// ---------------------------------------------------------------------------

export async function loginAction(input: unknown): Promise<ActionResult<{ redirectTo: string }>> {
  const parsed = loginSchema.safeParse(input);
  if (!parsed.success) {
    return { ok: false, code: 'VALIDATION', message: 'Enter your sign-in details.' };
  }
  try {
    const result = await authService.login(parsed.data);
    return {
      ok: true,
      data: { redirectTo: result.mustChangePassword ? '/settings/password?first=1' : '/dashboard' },
    };
  } catch (error) {
    const { normaliseError } = await import('@/lib/errors');
    return normaliseError(error) as ActionResult<never> & { ok: false };
  }
}

/**
 * Sign in as a seeded account with no password. Local development only — the
 * service refuses on anything but a loopback, non-production database, and it
 * re-checks here rather than trusting that the panel was rendered.
 */
export async function devLoginAction(account: string, _formData: FormData): Promise<void> {
  const { devSignIn } = await import('@/server/services/dev-login');
  const { redirectTo } = await devSignIn(account);
  redirect(redirectTo);
}

export async function logoutAction(): Promise<void> {
  const actor = await getActor();
  await authService.logout(actor ? { userId: actor.userId, organizationId: actor.organizationId } : null);
  redirect('/login');
}

export async function forgotPasswordAction(input: unknown): Promise<ActionResult<{ devToken?: string }>> {
  const parsed = forgotPasswordSchema.safeParse(input);
  if (!parsed.success) {
    return { ok: false, code: 'VALIDATION', message: 'Enter your email, username, or mobile number.' };
  }
  const result = await authService.requestPasswordReset(parsed.data.identifier);
  return {
    ok: true,
    data: result,
    message: 'If that account exists, a reset link has been created. Please contact the business owner if you do not receive it.',
  };
}

export async function resetPasswordAction(input: unknown): Promise<ActionResult<undefined>> {
  const parsed = resetPasswordSchema.safeParse(input);
  if (!parsed.success) {
    const { fieldErrorsFrom } = await import('@/lib/validation/common');
    return {
      ok: false,
      code: 'VALIDATION',
      message: 'Please check the highlighted fields.',
      fieldErrors: fieldErrorsFrom(parsed.error),
    };
  }
  try {
    await authService.completePasswordReset(parsed.data.token, parsed.data.password);
    return { ok: true, data: undefined, message: 'Your password has been changed. Please sign in.' };
  } catch (error) {
    const { normaliseError } = await import('@/lib/errors');
    return normaliseError(error) as ActionResult<never> & { ok: false };
  }
}

export const changePasswordAction = defineAction({
  name: 'changePassword',
  schema: changePasswordSchema,
  handler: async (input, { actor }) => {
    await authService.changePassword(
      { userId: actor.userId, organizationId: actor.organizationId },
      input.currentPassword,
      input.password,
    );
    return undefined;
  },
  successMessage: 'Your password has been changed.',
});

export const resetUserPasswordAction = defineAction({
  name: 'resetUserPassword',
  schema: z.object({ userId: idSchema, password: z.string().min(8).max(128) }),
  handler: async (input, { actor }) => {
    // The permission alone is not the control: whose password may be set is
    // decided inside the service, against the target's own row.
    await authService.setUserPassword(actor, input.userId, input.password);
    return undefined;
  },
  revalidate: ['/settings/users'],
  successMessage: 'Password reset. The user must change it at next sign-in.',
});

// ---------------------------------------------------------------------------
// Sales
// ---------------------------------------------------------------------------

export const createSaleAction = defineAction({
  name: 'createSale',
  schema: saleSchema,
  handler: (input, { actor, audit }) => createSale(actor, input, audit),
  revalidate: ['/sales', '/inventory', ...FINANCIAL_PATHS],
  successMessage: (output) => `Sale ${output.saleNumber} saved successfully.`,
});

export const voidSaleAction = defineAction({
  name: 'voidSale',
  schema: voidSaleSchema,
  handler: async (input, { actor, audit }) => {
    await voidSale(actor, input.saleId, input.reason, audit);
    return undefined;
  },
  revalidate: ['/sales', '/inventory', ...FINANCIAL_PATHS],
  successMessage: 'Sale voided. The stock and balances have been restored.',
});

export const requestCorrectionAction = defineAction({
  name: 'requestCorrection',
  schema: correctionRequestSchema,
  handler: (input, { actor, audit }) => requestCorrection(actor, input, audit),
  revalidate: ['/sales', '/sales/corrections'],
  successMessage: 'Correction request sent to your manager for review.',
});

export const reviewCorrectionAction = defineAction({
  name: 'reviewCorrection',
  schema: correctionReviewSchema,
  handler: (input, { actor, audit }) =>
    reviewCorrection(
      actor,
      { correctionId: input.correctionId, decision: input.decision, reviewNote: input.reviewNote },
      audit,
    ),
  revalidate: ['/sales', '/sales/corrections', ...FINANCIAL_PATHS],
  successMessage: (output) =>
    output.status === 'APPROVED' ? 'Correction approved and applied.' : 'Correction rejected. The sale is unchanged.',
});

// ---------------------------------------------------------------------------
// Purchases & suppliers
// ---------------------------------------------------------------------------

export const createPurchaseAction = defineAction({
  name: 'createPurchase',
  schema: purchaseSchema,
  handler: (input, { actor, audit }) => createPurchase(actor, input, audit),
  revalidate: ['/purchases', '/inventory', '/suppliers', ...FINANCIAL_PATHS],
  successMessage: (output) => `Purchase ${output.purchaseNumber} recorded and stock received.`,
});

export const voidPurchaseAction = defineAction({
  name: 'voidPurchase',
  schema: z.object({ purchaseId: idSchema, reason: z.string().trim().min(1).max(240) }),
  handler: async (input, { actor, audit }) => {
    await voidPurchase(actor, input.purchaseId, input.reason, audit);
    return undefined;
  },
  revalidate: ['/purchases', '/inventory', '/suppliers', ...FINANCIAL_PATHS],
  successMessage: 'Purchase voided.',
});

export const createSupplierAction = defineAction({
  name: 'createSupplier',
  schema: supplierSchema,
  handler: (input, { actor, audit }) => createSupplier(actor, input, audit),
  revalidate: ['/suppliers'],
  successMessage: 'Supplier added.',
});

export const updateSupplierAction = defineAction({
  name: 'updateSupplier',
  schema: supplierSchema.extend({ supplierId: idSchema }),
  handler: ({ supplierId, ...input }, { actor, audit }) => updateSupplier(actor, supplierId, input, audit),
  revalidate: ['/suppliers'],
  successMessage: 'Supplier updated.',
});

export const paySupplierAction = defineAction({
  name: 'paySupplier',
  schema: supplierPaymentSchema,
  handler: (input, { actor, audit }) => paySupplier(actor, input, audit),
  revalidate: ['/suppliers', ...FINANCIAL_PATHS],
  successMessage: (output) => `Payment ${output.paymentNumber} recorded.`,
});

// ---------------------------------------------------------------------------
// Employees
//
// Staff records, not logins. Nothing in this section writes to `User`, sets a
// password or grants a permission — that is `createUserAction` above, behind
// `user:manage`, and keeping them apart here is the same separation the service
// layer enforces.
// ---------------------------------------------------------------------------

export const createEmployeeAction = defineAction({
  name: 'createEmployee',
  schema: employeeSchema,
  handler: (input, { actor, audit }) => createEmployee(actor, input, audit),
  revalidate: ['/employees', '/salary'],
  successMessage: (output) => `${output.name} added to the staff list.`,
});

export const updateEmployeeAction = defineAction({
  name: 'updateEmployee',
  schema: employeeSchema.extend({ employeeId: idSchema }),
  handler: ({ employeeId, ...input }, { actor, audit }) => updateEmployee(actor, employeeId, input, audit),
  revalidate: ['/employees', '/salary'],
  successMessage: (output) => `${output.name} updated.`,
});

export const setEmployeeStatusAction = defineAction({
  name: 'setEmployeeStatus',
  schema: employeeStatusSchema,
  handler: ({ employeeId, status }, { actor, audit }) =>
    setEmployeeStatus(actor, employeeId, status, audit),
  revalidate: ['/employees', '/salary'],
  successMessage: (output) =>
    output.status === 'ACTIVE' ? `${output.name} is active again.` : `${output.name} has been deactivated.`,
});

// ---------------------------------------------------------------------------
// Customers
// ---------------------------------------------------------------------------

export const createCustomerAction = defineAction({
  name: 'createCustomer',
  schema: customerSchema,
  handler: (input, { actor, audit }) => createCustomer(actor, input, audit),
  revalidate: ['/customers'],
  successMessage: 'Customer added.',
});

export const updateCustomerAction = defineAction({
  name: 'updateCustomer',
  schema: customerSchema.extend({ customerId: idSchema }),
  handler: ({ customerId, ...input }, { actor, audit }) => updateCustomer(actor, customerId, input, audit),
  revalidate: ['/customers'],
  successMessage: 'Customer updated.',
});

export const receiveCustomerPaymentAction = defineAction({
  name: 'receiveCustomerPayment',
  schema: customerPaymentSchema,
  handler: (input, { actor, audit }) => receiveCustomerPayment(actor, input, audit),
  revalidate: ['/customers', '/sales', ...FINANCIAL_PATHS],
  successMessage: (output) => `Payment ${output.paymentNumber} received.`,
});

export const searchCustomersAction = defineQuery(
  async (input: { query: string }, { actor }) => searchCustomers(getDb(), actor, input.query),
  { name: 'searchCustomers' },
);

// ---------------------------------------------------------------------------
// Money out
// ---------------------------------------------------------------------------

export const createExpenseAction = defineAction({
  name: 'createExpense',
  schema: expenseSchema,
  handler: (input, { actor, audit }) => createExpense(actor, input, audit),
  revalidate: ['/expenses', ...FINANCIAL_PATHS],
  successMessage: 'Expense saved successfully.',
});

export const voidExpenseAction = defineAction({
  name: 'voidExpense',
  schema: z.object({ expenseId: idSchema, reason: z.string().trim().min(1).max(240) }),
  handler: async (input, { actor, audit }) => {
    await voidExpense(actor, input.expenseId, input.reason, audit);
    return undefined;
  },
  revalidate: ['/expenses', ...FINANCIAL_PATHS],
  successMessage: 'Expense voided.',
});

export const recordCommissionAction = defineAction({
  name: 'recordCommission',
  schema: gpCommissionSchema,
  handler: (input, { actor, audit }) => recordCommission(actor, input, audit),
  revalidate: ['/gp-commission', ...FINANCIAL_PATHS],
  successMessage: 'GP commission recorded.',
});

export const recordWithdrawalAction = defineAction({
  name: 'recordWithdrawal',
  schema: ownerWithdrawalSchema,
  handler: (input, { actor, audit }) => recordWithdrawal(actor, input, audit),
  revalidate: ['/withdrawals', ...FINANCIAL_PATHS],
  successMessage: 'Owner withdrawal recorded. This reduces cash but not profit.',
});

export const paySalaryAction = defineAction({
  name: 'paySalary',
  schema: salaryPaymentSchema,
  handler: (input, { actor, audit }) => paySalary(actor, input, audit),
  revalidate: ['/salary', '/expenses', ...FINANCIAL_PATHS],
  successMessage: 'Salary recorded and added to expenses.',
});

// ---------------------------------------------------------------------------
// KPI targets and payroll
// ---------------------------------------------------------------------------

const PAYROLL_PATHS = ['/payroll', '/employees', '/salary'];

export const saveKpiRuleAction = defineAction({
  name: 'saveKpiRule',
  schema: kpiRuleSchema,
  handler: (input, { actor, audit }) => saveKpiRule(actor, input, audit),
  revalidate: ['/payroll/targets', ...PAYROLL_PATHS],
  successMessage: (output) => `${output.name} saved.`,
});

export const saveEmployeeAllowanceAction = defineAction({
  name: 'saveEmployeeAllowance',
  schema: employeeAllowanceSchema,
  handler: (input, { actor, audit }) => saveEmployeeAllowance(actor, input, audit),
  revalidate: PAYROLL_PATHS,
  successMessage: 'Allowance saved. It will appear on every payslip from now on.',
});

export const removeEmployeeAllowanceAction = defineAction({
  name: 'removeEmployeeAllowance',
  schema: z.object({ allowanceId: idSchema }),
  handler: ({ allowanceId }, { actor, audit }) => removeEmployeeAllowance(actor, allowanceId, audit),
  revalidate: PAYROLL_PATHS,
  successMessage: 'Allowance removed from future payslips.',
});

export const draftPayslipAction = defineAction({
  name: 'draftPayslip',
  schema: payslipDraftSchema,
  handler: (input, { actor, audit }) => draftPayslip(actor, input, audit),
  revalidate: PAYROLL_PATHS,
  successMessage: (output) => `Payslip saved. Net pay ${output.netPay}.`,
});

export const draftBranchPayrollAction = defineAction({
  name: 'draftBranchPayroll',
  schema: z.object({ branchId: idSchema, periodKey: monthKeySchema }),
  handler: (input, { actor, audit }) => draftBranchPayroll(actor, input, audit),
  revalidate: PAYROLL_PATHS,
  successMessage: (output) =>
    output.skipped.length === 0
      ? `Payroll drafted for ${output.drafted} employee${output.drafted === 1 ? '' : 's'}.`
      : `Payroll drafted for ${output.drafted}. ${output.skipped.length} already signed off and left alone.`,
});

export const finalizePayslipAction = defineAction({
  name: 'finalizePayslip',
  schema: payslipFinalizeSchema,
  handler: ({ payslipId }, { actor, audit }) => finalizePayslip(actor, payslipId, audit),
  revalidate: PAYROLL_PATHS,
  successMessage: 'Payslip finalised. It is now locked against editing.',
});

export const payPayslipAction = defineAction({
  name: 'payPayslip',
  schema: payslipPaySchema,
  handler: (input, { actor, audit }) => payPayslip(actor, input, audit),
  revalidate: [...PAYROLL_PATHS, '/expenses', ...FINANCIAL_PATHS],
  successMessage: (output) => `Paid ${output.paidAmount}. Recorded as ${output.salaryNumber}.`,
});

export const reopenPayslipAction = defineAction({
  name: 'reopenPayslip',
  schema: payslipReopenSchema,
  handler: (input, { actor, audit }) => reopenPayslip(actor, input, audit),
  revalidate: PAYROLL_PATHS,
  successMessage: (output) =>
    `Payslip reopened as version ${output.version}. The figures it held are kept on the record.`,
});

export const cancelPayslipAction = defineAction({
  name: 'cancelPayslip',
  schema: payslipCancelSchema,
  handler: (input, { actor, audit }) => cancelPayslip(actor, input, audit),
  revalidate: PAYROLL_PATHS,
  successMessage: 'Payslip cancelled.',
});

// ---------------------------------------------------------------------------
// Returns & adjustments
// ---------------------------------------------------------------------------

export const createReturnAction = defineAction({
  name: 'createReturn',
  schema: saleReturnSchema,
  handler: (input, { actor, audit }) => createReturn(actor, input, audit),
  revalidate: ['/returns', '/sales', '/inventory', ...FINANCIAL_PATHS],
  successMessage: (output) => `Return ${output.returnNumber} recorded.`,
});

export const createAdjustmentAction = defineAction({
  name: 'createAdjustment',
  schema: inventoryAdjustmentSchema,
  handler: (input, { actor, audit }) => createAdjustment(actor, input, audit),
  revalidate: ['/adjustments', '/inventory', ...FINANCIAL_PATHS],
  successMessage: 'Inventory adjusted.',
});


// ---------------------------------------------------------------------------
// Day-end physical inventory
// ---------------------------------------------------------------------------

export const recordStockCountAction = defineAction({
  name: 'recordStockCount',
  schema: stockCountEntrySchema,
  handler: (input, { actor, audit }) => recordStockCount(actor, input, audit),
  revalidate: ['/inventory/day-end', '/inventory'],
  successMessage: (output) =>
    output.withVariance === 0
      ? `${output.counted} product${output.counted === 1 ? '' : 's'} counted. Everything matches.`
      : `${output.counted} product${output.counted === 1 ? '' : 's'} counted, ${output.withVariance} with a difference.`,
});

export const finalizeStockCountAction = defineAction({
  name: 'finalizeStockCount',
  schema: stockCountFinalizeSchema,
  // The corrections it posts move stock balances, so the inventory screens and
  // the adjustment list are all now out of date.
  revalidate: ['/inventory/day-end', '/inventory', '/adjustments', '/daily-closing', '/reports'],
  handler: (input, { actor, audit }) => finalizeStockCount(actor, input, audit),
  successMessage: (output) =>
    output.netVarianceUnits === 0 && output.shortageUnits === 0
      ? `Stock count finalised. All ${output.productsCounted} counted products match the books.`
      : `Stock count finalised. ${output.shortageUnits} unit${output.shortageUnits === 1 ? '' : 's'} short, ${output.overageUnits} over — ${output.correctionsPosted} correction${output.correctionsPosted === 1 ? '' : 's'} posted to the stock ledger.`,
});

export const amendStockCountAction = defineAction({
  name: 'amendStockCount',
  schema: stockCountAmendSchema,
  handler: (input, { actor, audit }) => amendStockCountLine(actor, input, audit),
  revalidate: ['/inventory/day-end', '/inventory', '/adjustments', '/audit'],
  successMessage: (output) =>
    `${output.productName} corrected to ${output.quantity}. The change is recorded as version ${output.version}.`,
});

// ---------------------------------------------------------------------------
// Day control
// ---------------------------------------------------------------------------

export const openDayAction = defineAction({
  name: 'openDay',
  schema: dailyOpeningSchema,
  handler: (input, { actor, audit }) => openDay(actor, input, audit),
  revalidate: ['/daily-closing', '/dashboard'],
  successMessage: 'Day opened. You can start recording sales.',
});

export const closeDayAction = defineAction({
  name: 'closeDay',
  schema: dailyClosingSchema,
  handler: (input, { actor, audit }) => closeDay(actor, input, audit),
  revalidate: ['/daily-closing', ...FINANCIAL_PATHS],
  successMessage: (output) =>
    output.state === 'balanced'
      ? 'Day closed. The cash matches exactly.'
      : `Day closed. Cash is ${output.state === 'short' ? 'short' : 'over'} by ${output.cashDifference.replace('-', '')}.`,
});

/**
 * Signing off a day that was missed.
 *
 * Its own action rather than a flag on `closeDay`, so that the thing a caller
 * has to reach for in order to backdate a closing is visibly a different thing,
 * with a different permission and a mandatory reason on its schema. A request
 * that puts an old date into the ordinary action is refused by the service,
 * whatever the browser sent.
 */
export const closeDayLateAction = defineAction({
  name: 'closeDayLate',
  schema: lateDailyClosingSchema,
  handler: (input, { actor, audit }) => closeDayLate(actor, input, audit),
  revalidate: ['/daily-closing', ...FINANCIAL_PATHS],
  successMessage: (output) =>
    `Day closed late and recorded for audit. Cash is ${output.state === 'balanced' ? 'exact' : output.state === 'short' ? `short by ${output.cashDifference.replace('-', '')}` : `over by ${output.cashDifference}`}.`,
});

export const closeAgentTillAction = defineAction({
  name: 'closeAgentTill',
  schema: agentClosingSchema,
  handler: (input, { actor, audit }) => closeAgentDay(actor, input, audit),
  revalidate: ['/daily-closing', '/dashboard'],
  successMessage: (output) =>
    output.cashDifference.toString() === '0'
      ? 'Till counted. It matches exactly.'
      : `Till counted. It is ${output.cashDifference.toString().startsWith('-') ? 'short' : 'over'} by ${output.cashDifference.toString().replace('-', '')}.`,
});

export const reopenDayAction = defineAction({
  name: 'reopenDay',
  schema: reopenDaySchema,
  handler: async (input, { actor, audit }) => {
    await reopenDay(actor, input, audit);
    return undefined;
  },
  revalidate: ['/daily-closing', ...FINANCIAL_PATHS],
  successMessage: 'Day reopened for corrections. Remember to close it again.',
});

// ---------------------------------------------------------------------------
// Closing corrections
// ---------------------------------------------------------------------------

export const requestClosingCorrectionAction = defineAction({
  name: 'requestClosingCorrection',
  schema: closingCorrectionRequestSchema,
  handler: (input, { actor, audit }) => requestClosingCorrection(actor, input, audit),
  revalidate: ['/daily-closing', '/dashboard'],
  successMessage: (output) =>
    `Correction requested for ${output.businessDate}. Your manager will review it.`,
});

export const reviewClosingCorrectionAction = defineAction({
  name: 'reviewClosingCorrection',
  schema: closingCorrectionReviewSchema,
  handler: (input, { actor, audit }) => reviewClosingCorrection(actor, input, audit),
  // Approval reopens the day, which moves what every closing-derived screen says.
  revalidate: ['/daily-closing', '/audit', ...FINANCIAL_PATHS],
  successMessage: (output) =>
    output.decision === 'APPROVE'
      ? output.dayReopened
        ? `Approved. ${output.dateLabel} is reopened — count the drawer again and close it.`
        : `Approved. ${output.dateLabel} still needs closing.`
      : output.decision === 'REJECT'
        ? `Rejected. ${output.dateLabel} is unchanged.`
        : 'More information requested. The agent has been told what is needed.',
});

export const resubmitClosingCorrectionAction = defineAction({
  name: 'resubmitClosingCorrection',
  schema: closingCorrectionResubmitSchema,
  handler: (input, { actor, audit }) => resubmitClosingCorrection(actor, input, audit),
  revalidate: ['/daily-closing'],
  successMessage: 'Sent. Your manager will take another look.',
});

export const cancelClosingCorrectionAction = defineAction({
  name: 'cancelClosingCorrection',
  schema: z.object({ requestId: idSchema }),
  handler: (input, { actor, audit }) => cancelClosingCorrection(actor, input, audit),
  revalidate: ['/daily-closing'],
  successMessage: 'Request withdrawn.',
});

// ---------------------------------------------------------------------------
// Financial accounts
// ---------------------------------------------------------------------------

/** Paths whose figures move when an account balance does. */
const ACCOUNT_PATHS = ['/accounts', '/dashboard', '/daily-closing'];

export const createAccountAction = defineAction({
  name: 'createAccount',
  schema: financialAccountSchema,
  handler: (input, { actor, audit }) => createAccount(actor, input, audit),
  revalidate: ACCOUNT_PATHS,
  successMessage: (output) => `${output.name} added.`,
});

export const updateAccountAction = defineAction({
  name: 'updateAccount',
  schema: financialAccountSchema.extend({ id: idSchema }),
  handler: (input, { actor, audit }) => updateAccount(actor, input, audit),
  revalidate: ACCOUNT_PATHS,
  successMessage: (output) => `${output.name} updated.`,
});

export const createTransferAction = defineAction({
  name: 'createTransfer',
  schema: accountTransferSchema,
  handler: (input, { actor, audit }) => createTransfer(actor, input, audit),
  revalidate: ACCOUNT_PATHS,
  // Naming both sides matters: the point of a transfer is that the money is
  // still there, in a different place.
  successMessage: (output) =>
    `Moved ৳${output.amount} from ${output.fromAccountName} to ${output.toAccountName}.`,
});

export const voidTransferAction = defineAction({
  name: 'voidTransfer',
  schema: voidTransferSchema,
  handler: (input, { actor, audit }) => voidTransfer(actor, input, audit),
  revalidate: ACCOUNT_PATHS,
  successMessage: 'Transfer voided. Both accounts have been put back.',
});

export const adjustAccountAction = defineAction({
  name: 'adjustAccount',
  schema: accountAdjustmentSchema,
  handler: (input, { actor, audit }) => adjustAccount(actor, input, audit),
  revalidate: ACCOUNT_PATHS,
  successMessage: (output) => `Adjustment recorded. The balance is now ৳${output.balanceAfter}.`,
});

// ---------------------------------------------------------------------------
// Stored-value floats
// ---------------------------------------------------------------------------

export const topUpFloatAction = defineAction({
  name: 'topUpFloat',
  schema: floatMovementSchema,
  handler: (input, { actor, audit }) => topUpFloat(actor, input, audit),
  revalidate: [...ACCOUNT_PATHS, '/float'],
  successMessage: (output) =>
    `${output.accountName} topped up. The balance is now ৳${output.balanceAfter}.`,
});

export const consumeFloatAction = defineAction({
  name: 'consumeFloat',
  schema: floatMovementSchema,
  handler: (input, { actor, audit }) => consumeFloat(actor, input, audit),
  revalidate: [...ACCOUNT_PATHS, '/float'],
  successMessage: (output) => `Recorded. ${output.accountName} now holds ৳${output.balanceAfter}.`,
});

// ---------------------------------------------------------------------------
// GP commission receivable
// ---------------------------------------------------------------------------

export const settleCommissionAction = defineAction({
  name: 'settleCommission',
  schema: commissionSettlementSchema,
  handler: (input, { actor, audit }) => settleCommission(actor, input, audit),
  revalidate: ['/gp-commission', ...FINANCIAL_PATHS],
  successMessage: (output) =>
    output.status === 'RECEIVED'
      ? `${output.commissionNumber} is now settled in full.`
      : `Recorded. ৳${output.outstanding} is still outstanding on ${output.commissionNumber}.`,
});

export const voidCommissionSettlementAction = defineAction({
  name: 'voidCommissionSettlement',
  schema: z.object({ settlementId: idSchema, reason: z.string().trim().min(1).max(240) }),
  handler: (input, { actor, audit }) => voidSettlement(actor, input, audit),
  revalidate: ['/gp-commission', ...FINANCIAL_PATHS],
  successMessage: 'Settlement voided. The commission is outstanding again.',
});

// ---------------------------------------------------------------------------
// Supplier returns
// ---------------------------------------------------------------------------

export const createSupplierReturnAction = defineAction({
  name: 'createSupplierReturn',
  schema: supplierReturnSchema,
  handler: (input, { actor, audit }) => createSupplierReturn(actor, input, audit),
  revalidate: ['/returns', '/suppliers', '/inventory', ...FINANCIAL_PATHS],
  successMessage: (output) =>
    `${output.returnNumber} recorded. ${output.supplierName} is now owed ৳${output.supplierBalanceAfter}.`,
});

export const voidSupplierReturnAction = defineAction({
  name: 'voidSupplierReturn',
  schema: voidSupplierReturnSchema,
  handler: (input, { actor, audit }) => voidSupplierReturn(actor, input, audit),
  revalidate: ['/returns', '/suppliers', '/inventory', ...FINANCIAL_PATHS],
  successMessage: 'Return voided. The stock and the supplier balance have been put back.',
});

// ---------------------------------------------------------------------------
// House returns and replacements
// ---------------------------------------------------------------------------

/**
 * Every one of these revalidates `/adjustments` as well as its own screen.
 *
 * A faulty unit is usually found there, and a claim raised against it changes
 * what that page should show — leaving it stale is how somebody sends the same
 * dead handset to the House twice.
 */
const HOUSE_RETURN_PATHS = ['/house-returns', '/returns', '/adjustments', '/inventory', '/suppliers'];

export const sendToHouseAction = defineAction({
  name: 'sendToHouse',
  schema: sendToHouseSchema,
  handler: (input, { actor, audit }) => sendToHouse(actor, input, audit),
  revalidate: HOUSE_RETURN_PATHS,
  successMessage: (output) =>
    output.stockRemoved
      ? `${output.returnNumber} sent to ${output.supplierName}. ${output.quantity} × ${output.productName} is out of sellable stock and pending replacement.`
      : `${output.returnNumber} sent to ${output.supplierName}. The unit was already written off, so stock is unchanged — it is now pending replacement.`,
});

export const receiveReplacementAction = defineAction({
  name: 'receiveReplacement',
  schema: receiveReplacementSchema,
  // The difference lands on an account balance or in a drawer, so the financial
  // screens have to be revalidated too.
  revalidate: [...HOUSE_RETURN_PATHS, ...FINANCIAL_PATHS],
  handler: (input, { actor, audit }) => receiveReplacement(actor, input, audit),
  successMessage: (output) => {
    const received = `${output.replacementProductName} received against ${output.returnNumber}.`;
    if (output.settlement === 'NONE') return `${received} Like-for-like, so nothing to settle.`;

    const difference = output.valueDifference.startsWith('-')
      ? `${output.replacementProductName} is ৳${output.valueDifference.slice(1)} cheaper`
      : `${output.replacementProductName} is ৳${output.valueDifference} dearer`;
    return output.settlement === 'CASH'
      ? `${received} ${difference}, settled in cash.`
      : `${received} ${difference} — ${output.supplierName} is now owed ৳${output.supplierBalanceAfter}.`;
  },
});

export const cancelHouseReturnAction = defineAction({
  name: 'cancelHouseReturn',
  schema: cancelHouseReturnSchema,
  handler: (input, { actor, audit }) => cancelHouseReturn(actor, input, audit),
  revalidate: HOUSE_RETURN_PATHS,
  successMessage: (output) =>
    output.restoredToStock
      ? `${output.returnNumber} called off. The unit is back in stock.`
      : `${output.returnNumber} called off. No replacement is expected.`,
});

/**
 * Faulty customer-return lines that have not been claimed yet.
 *
 * A query rather than page data for the same reason the purchase picker is one:
 * which lines are relevant depends on the Customer Point chosen inside the
 * dialogue, which the server did not know when the page was rendered.
 */
export const faultyAwaitingHouseAction = defineQuery(
  async (input: { branchId: string }, { actor }) => faultyAwaitingHouse(getDb(), actor, input),
  { name: 'faultyAwaitingHouse' },
);

// ---------------------------------------------------------------------------
// Onboarding
// ---------------------------------------------------------------------------

export const recordOpeningBalancesAction = defineAction({
  name: 'recordOpeningBalances',
  schema: openingBalancesSchema,
  handler: (input, { actor, audit }) => recordOpeningBalances(actor, input, audit),
  revalidate: ['/onboarding', '/customers', '/suppliers', '/inventory', ...FINANCIAL_PATHS],
  successMessage: (output) => {
    const recorded = `Opening balances recorded: ${output.accounts} account${output.accounts === 1 ? '' : 's'}, ${output.customers} customer due${output.customers === 1 ? '' : 's'}, ${output.stockLines + output.handsets} stock line${output.stockLines + output.handsets === 1 ? '' : 's'}.`;
    // A row the service declined to carry in — a duplicate IMEI, a product that
    // is not IMEI-tracked — used to vanish into the return value. A shop owner
    // reading "recorded" while three handsets were dropped has an opening stock
    // that is wrong and no reason to suspect it.
    if (output.skipped.length === 0) return recorded;
    return `${recorded} ${output.skipped.length} row${output.skipped.length === 1 ? ' was' : 's were'} not carried in — see below.`;
  },
});

// ---------------------------------------------------------------------------
// Store credit
// ---------------------------------------------------------------------------

export const redeemStoreCreditAction = defineAction({
  name: 'redeemStoreCredit',
  schema: redeemStoreCreditSchema,
  handler: (input, { actor, audit }) => redeemStoreCreditService(actor, input, audit),
  revalidate: ['/customers', ...FINANCIAL_PATHS],
  successMessage: (output) => `Credit used. ৳${output.balanceAfter} remaining.`,
});

// ---------------------------------------------------------------------------
// Data import
// ---------------------------------------------------------------------------

export const startImportAction = defineAction({
  name: 'startImport',
  schema: startImportSchema,
  handler: (input, { actor, audit }) => startImport(actor, input, audit),
  successMessage: (output) => `${output.totalRows} rows read. Now match the columns.`,
});

export const validateImportAction = defineAction({
  name: 'validateImport',
  schema: mapImportSchema,
  handler: (input, { actor }) => mapAndValidate(actor, input),
  successMessage: (output) =>
    output.invalidRows === 0
      ? `All ${output.validRows} rows look good.`
      : `${output.validRows} rows are ready and ${output.invalidRows} need attention.`,
});

export const commitImportAction = defineAction({
  name: 'commitImport',
  schema: commitImportSchema,
  handler: (input, { actor, audit }) => commitImport(actor, input, audit),
  revalidate: [
    '/import',
    '/settings/products',
    '/inventory',
    '/customers',
    '/suppliers',
    ...FINANCIAL_PATHS,
  ],
  successMessage: (output) =>
    output.failed > 0
      ? `${output.summary}. ${output.failed} row${output.failed === 1 ? '' : 's'} could not be imported.`
      : output.summary,
});

export const cancelImportAction = defineAction({
  name: 'cancelImport',
  schema: cancelImportSchema,
  handler: async (input, { actor, audit }) => {
    await cancelImport(actor, input.batchId, audit);
    return undefined;
  },
  revalidate: ['/import'],
  successMessage: 'Import cancelled. Nothing was changed.',
});

// ---------------------------------------------------------------------------
// Catalogue & organisation
// ---------------------------------------------------------------------------

export const createProductAction = defineAction({
  name: 'createProduct',
  schema: productSchema,
  handler: (input, { actor, audit }) => createProduct(actor, input, audit),
  revalidate: ['/settings/products', '/inventory'],
  successMessage: 'Product added.',
});

export const updateProductAction = defineAction({
  name: 'updateProduct',
  schema: z.object({ productId: idSchema, data: productSchema }),
  handler: (input, { actor, audit }) => updateProduct(actor, input.productId, input.data, audit),
  revalidate: ['/settings/products', '/inventory'],
  successMessage: 'Product updated.',
});

export const searchProductsAction = defineQuery(
  async (input: { branchId: string; query: string; favouritesOnly?: boolean }, { actor }) =>
    searchSellableProducts(getDb(), actor, input.branchId, input.query, {
      favouritesOnly: input.favouritesOnly ?? false,
    }),
  { name: 'searchProducts' },
);

/**
 * The supplier's recent invoices, for "which purchase is this against?".
 *
 * A query rather than page data because the answer depends on the supplier and
 * the Customer Point chosen inside the dialogue, which the server did not know
 * when the page was rendered.
 */
export const returnablePurchasesAction = defineQuery(
  async (input: { supplierId: string; branchId: string }, { actor }) =>
    (await returnablePurchases(getDb(), actor, input)).map((purchase) => ({
      id: purchase.id,
      purchaseNumber: purchase.purchaseNumber,
      invoiceNumber: purchase.invoiceNumber,
      businessDate: purchase.businessDate,
      total: toAmountString(purchase.total),
    })),
  { name: 'returnablePurchases' },
);

export const lookupImeiAction = defineQuery(
  async (input: { imei: string }, { actor }) => findHandsetByImei(getDb(), actor, input.imei),
  { name: 'lookupImei' },
);

export const createBranchAction = defineAction({
  name: 'createBranch',
  schema: branchSchema,
  handler: (input, { actor, audit }) => createBranch(actor, input, audit),
  revalidate: ['/settings/branches', '/dashboard'],
  successMessage: 'Customer Point created.',
});

export const updateBranchAction = defineAction({
  name: 'updateBranch',
  schema: branchSchema.extend({ branchId: idSchema }),
  handler: ({ branchId, ...input }, { actor, audit }) => updateBranch(actor, branchId, input, audit),
  revalidate: ['/settings/branches', '/dashboard'],
  successMessage: 'Customer Point updated.',
});

export const createUserAction = defineAction({
  name: 'createUser',
  schema: createUserSchema,
  handler: (input, { actor, audit }) => createUser(actor, input, audit),
  revalidate: ['/settings/users', '/agents'],
  successMessage: 'User created.',
});

export const updateUserAction = defineAction({
  name: 'updateUser',
  schema: updateUserSchema,
  handler: (input, { actor, audit }) => updateUser(actor, input, audit),
  revalidate: ['/settings/users', '/agents', '/dashboard'],
  // Said plainly, because being signed out of a working till without warning
  // reads as a fault rather than as the consequence it is.
  successMessage: (output) =>
    output.signedOut
      ? `${output.name} updated. They have been signed out, because what they may do has changed.`
      : `${output.name} updated.`,
});

export const setUserStatusAction = defineAction({
  name: 'setUserStatus',
  schema: z.object({ userId: idSchema, status: z.enum(['ACTIVE', 'INACTIVE']) }),
  handler: (input, { actor, audit }) => setUserStatus(actor, input.userId, input.status, audit),
  revalidate: ['/settings/users', '/agents'],
  successMessage: (output) => (output.status === 'ACTIVE' ? 'User reactivated.' : 'User deactivated.'),
});

export const setAgentTargetAction = defineAction({
  name: 'setAgentTarget',
  schema: agentTargetSchema,
  handler: (input, { actor, audit }) => setAgentTarget(actor, input, audit),
  revalidate: ['/agents'],
  successMessage: 'Sales target saved.',
});

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export const updateSettingsAction = defineAction({
  name: 'updateSettings',
  schema: z.object({
    key: z.enum(SETTINGS_KEYS as unknown as [SettingsKey, ...SettingsKey[]]),
    value: z.record(z.string(), z.unknown()),
  }),
  handler: async (input, { actor, audit }) => {
    assertCan(actor, 'settings:manage');
    return transaction((tx) => updateSettings(tx, audit, input.key, input.value));
  },
  revalidate: ['/settings'],
  successMessage: 'Settings saved.',
});

export const updateOrganizationAction = defineAction({
  name: 'updateOrganization',
  schema: z.object({
    name: z.string().trim().min(1).max(120),
    ownerName: z.string().trim().min(1).max(120),
    phone: z.string().trim().min(1).max(20),
    email: z.string().trim().email().optional().or(z.literal('')),
    address: z.string().trim().max(240).optional(),
    district: z.string().trim().max(60).optional(),
  }),
  handler: async (input, { actor, audit }) => {
    assertCan(actor, 'settings:manage');
    return transaction(async (tx) => {
      const before = await tx.organization.findUniqueOrThrow({ where: { id: actor.organizationId } });
      const updated = await tx.organization.update({
        where: { id: actor.organizationId },
        data: {
          name: input.name,
          ownerName: input.ownerName,
          phone: input.phone,
          email: input.email || null,
          address: input.address ?? null,
          district: input.district ?? null,
        },
        select: { id: true, name: true },
      });
      const { recordAudit, AuditAction } = await import('@/lib/audit');
      await recordAudit(tx, audit, {
        action: AuditAction.SETTINGS_CHANGED,
        entityType: 'Organization',
        entityId: actor.organizationId,
        summary: 'Updated business information',
        oldValue: { name: before.name, ownerName: before.ownerName, phone: before.phone },
        newValue: input,
      });
      return updated;
    });
  },
  revalidate: ['/settings'],
  successMessage: 'Business information saved.',
});

// ---------------------------------------------------------------------------
// Offline sync & notifications
// ---------------------------------------------------------------------------

export const resolveConflictAction = defineAction({
  name: 'resolveConflict',
  schema: resolveConflictSchema,
  handler: async (input, { actor, audit }) => {
    await resolveSyncConflict(actor, input, audit);
    return undefined;
  },
  revalidate: ['/sync'],
  successMessage: 'Conflict resolved.',
});

export const markNotificationReadAction = defineAction({
  name: 'markNotificationRead',
  schema: z.object({ notificationId: idSchema }),
  handler: async (input, { actor }) => {
    await markNotificationRead(getDb(), actor.organizationId, input.notificationId, {
      userId: actor.userId,
      role: actor.role,
      branchIds: actor.branchIds,
    });
    return undefined;
  },
  revalidate: ['/notifications'],
});

export const markAllNotificationsReadAction = defineAction({
  name: 'markAllNotificationsRead',
  schema: z.object({}),
  handler: async (_input, { actor }) => {
    await markAllNotificationsRead(getDb(), actor.organizationId, actor.userId, actor.role, actor.branchIds);
    return undefined;
  },
  successMessage: 'All notifications marked as read.',
});

/** Switch the active Customer Point. Stored in a cookie so it survives a reload. */
export async function setActiveBranchAction(branchId: string | null): Promise<void> {
  const actor = await getActor();
  if (!actor) throw errors.unauthenticated();

  const { cookies } = await import('next/headers');
  const store = await cookies();

  if (!branchId) {
    if (actor.role !== 'OWNER') throw errors.branchForbidden();
    store.delete('cpcel_branch');
    return;
  }

  if (actor.role !== 'OWNER' && !actor.branchIds.includes(branchId)) throw errors.branchForbidden();
  store.set('cpcel_branch', branchId, {
    httpOnly: false,
    sameSite: 'lax',
    path: '/',
    maxAge: 60 * 60 * 24 * 365,
  });
}

// ---------------------------------------------------------------------------
// Subscription and billing (Phase 2)
// ---------------------------------------------------------------------------

/**
 * A customer tells CPCel they have paid.
 *
 * Records a *claim*, not a settlement. Nothing about the subscription changes
 * until a CPCel finance administrator confirms the money arrived, and the wording
 * of the confirmation message says so rather than implying the invoice is closed.
 */
export const reportSubscriptionPaymentAction = defineAction({
  name: 'reportSubscriptionPayment',
  schema: reportSubscriptionPaymentSchema,
  handler: (input, { actor, audit }) => reportPayment(actor, input, audit),
  revalidate: ['/settings/subscription'],
  successMessage:
    'Thank you — we have recorded your payment details and will confirm once the money is received.',
});

export const cancelSubscriptionAction = defineAction({
  name: 'cancelSubscription',
  schema: cancelSubscriptionSchema,
  handler: (input, { actor }) => cancelSubscription(actor, input),
  revalidate: ['/settings/subscription', '/settings'],
  successMessage: (output) =>
    `Your subscription is cancelled with effect from ${formatDate(output.effectiveDate)}. Your data stays available until at least ${formatDate(output.retentionUntil)}.`,
});

export const reactivateSubscriptionAction = defineAction({
  name: 'reactivateSubscription',
  schema: reactivateSubscriptionSchema,
  handler: (input, { actor }) => reactivateSubscription(actor, input),
  revalidate: ['/settings/subscription', '/settings'],
  successMessage: 'Welcome back — your subscription is active again.',
});

// ---------------------------------------------------------------------------
// CPCel support access (Phase 2)
// ---------------------------------------------------------------------------

/**
 * The owner opens the door.
 *
 * Note the success message names the expiry. Access that the owner cannot see the
 * end of is access they have not really agreed to.
 */
export const grantSupportAccessAction = defineAction({
  name: 'grantSupportAccess',
  schema: grantSupportAccessSchema,
  handler: (input, { actor }) => grantSupportAccess(actor, input),
  revalidate: ['/settings/security'],
  successMessage: (output) =>
    output.expiresAt
      ? `CPCel support can ${output.allowWrites ? 'view and change' : 'view'} your data until ${formatDateTime(output.expiresAt)}.`
      : 'CPCel support access is on.',
});

export const revokeSupportAccessAction = defineAction({
  name: 'revokeSupportAccess',
  schema: revokeSupportAccessSchema,
  handler: (input, { actor }) => revokeSupportAccess(actor, input),
  revalidate: ['/settings/security'],
  successMessage: 'CPCel support access is off, and any open session has been ended.',
});

// ---------------------------------------------------------------------------
// Data export (Phase 2)
// ---------------------------------------------------------------------------

export const requestDataExportAction = defineAction({
  name: 'requestDataExport',
  schema: dataExportSchema,
  handler: (input, { actor }) =>
    requestExport(actor, {
      datasets: input.datasets,
      format: input.format,
      fromDate: input.fromDate ?? null,
      toDate: input.toDate ?? null,
    }),
  revalidate: ['/settings/data'],
  successMessage: 'Your export is ready to download.',
});
